import { PageController } from '@client/components/_core/layout/PageController';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { createFileRoute } from '@tanstack/react-router';
import { Card } from 'antd';
import clsx from 'clsx';
import type { LucideIcon } from 'lucide-react';
import { ChartBar, Globe, Image, Key, Mail, Phone } from 'lucide-react';
import { useState } from 'react';
import { AzureADConfig } from './AzureADConfig';
import { EmailConfig } from './EmailConfig';
import { LogoConfig } from './LogoConfig';
import { PhoneNumber } from './PhoneNumber';
import { Reporting } from './Reporting';

export const Route = createFileRoute('/console/$orgId/configuration/org-settings/system')({
    staticData: {
        metadata: {
            title: 'System',
        },
        permissions: ['settings:update'],
        ui: {
            icon: Globe,
            title: 'System configuration',
            subtitle:
                "Manage your organisation's core settings and preferences. Configure email settings, branding, reporting preferences, and authentication options.",
            menuDescription: 'Configure email, branding, and authentication',
        },
    },
    component: ConfigurationSystem,
});

type SectionKey = 'email' | 'logo' | 'reporting' | 'phone' | 'azure-ad';

function ConfigurationSystem() {
    const orgId = useOrgId();
    const { trpc } = useTrpc();
    const { data: org } = trpc.org.getOrg.useQuery({ orgId });
    const [section, setSection] = useState<SectionKey>('email');

    // Check if Azure AD is enabled for this organisation
    const isAzureADEnabled = org?.enabledAuth0Connections?.includes('curium-ad');

    const navItems: { key: SectionKey; label: string; icon: LucideIcon }[] = [
        { key: 'email', label: 'E-mail', icon: Mail },
        { key: 'logo', label: 'Logo', icon: Image },
        { key: 'reporting', label: 'Reporting', icon: ChartBar },
        { key: 'phone', label: 'Phone number', icon: Phone },
        ...(isAzureADEnabled ? [{ key: 'azure-ad' as const, label: 'Azure AD', icon: Key }] : []),
    ];

    return (
        <PageController route={Route}>
            <Card variant="outlined" className="mt-4 max-w-[1064px] overflow-hidden" styles={{ body: { padding: 0 } }}>
                <div className="grid grid-cols-1 md:grid-cols-[232px_1fr]">
                    <nav className="flex flex-col gap-1 border-separator border-b bg-card-header-bg px-3 py-4 md:border-r md:border-b-0">
                        <span className="px-2.5 pt-1 pb-2 font-semibold text-subtle-foreground text-xs uppercase tracking-wider">
                            Settings
                        </span>
                        {navItems.map(({ key, label, icon: Icon }) => {
                            const active = section === key;
                            return (
                                <button
                                    key={key}
                                    type="button"
                                    onClick={() => setSection(key)}
                                    aria-current={active ? 'page' : undefined}
                                    className={clsx(
                                        'flex items-center gap-2.5 rounded-md px-3 py-2.5 text-left font-medium text-sm transition-colors',
                                        active
                                            ? 'bg-accent-100 text-accent-700'
                                            : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                                    )}
                                >
                                    <Icon size={17} className="shrink-0" />
                                    {label}
                                </button>
                            );
                        })}
                    </nav>
                    <div className="min-w-0 px-8 py-7">
                        {/* Status cards / descriptions fill this column; the forms inside cap
                            themselves narrower (620px) for the intentional two-tier design look. */}
                        {/* Every section stays mounted (toggled with `hidden`) so unsaved form
                            edits and fetched state survive switching sections, mirroring the
                            previous Tabs behaviour. */}
                        <div className="max-w-3xl">
                            <div className={section === 'email' ? undefined : 'hidden'}>
                                <EmailConfig />
                            </div>
                            <div className={section === 'logo' ? undefined : 'hidden'}>
                                <LogoConfig />
                            </div>
                            <div className={section === 'reporting' ? undefined : 'hidden'}>
                                <Reporting />
                            </div>
                            <div className={section === 'phone' ? undefined : 'hidden'}>
                                <PhoneNumber />
                            </div>
                            {isAzureADEnabled && (
                                <div className={section === 'azure-ad' ? undefined : 'hidden'}>
                                    <AzureADConfig />
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </Card>
        </PageController>
    );
}
